import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectUserCountry } from 'store/auth/selectors';

import { AirtableService } from '../../shared/airtable';
import { AIRTABLE_CONFIG } from '../../shared/config';
import { AirtableBase } from '../../shared/types';
import { getDataAsMenuOptions } from '../../util';

const Airtable = new AirtableService(
  process.env.REACT_APP_AIRTABLE_API_KEY || '',
);

const useBaseTablesQuery = (baseId: string | null, country?: string) => {
  const userCountry = useSelector(selectUserCountry);

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['base-tables', { baseId, country, userCountry }],
    queryFn: () => Airtable.getBaseTables(baseId || ''),
    staleTime: 1000 * 10,
    enabled: !!baseId,
  });

  const menuOptions = useMemo(() => {
    if (!data) return [];
    return getDataAsMenuOptions(data, 'name', 'id')?.filter((item) => {
      const countryBases =
        AIRTABLE_CONFIG?.[
          (country as keyof typeof AIRTABLE_CONFIG) ??
            (userCountry as keyof typeof AIRTABLE_CONFIG)
        ]?.bases;

      const targetBase = countryBases?.[
        baseId as keyof typeof countryBases
      ] as AirtableBase;

      const targetBaseTables = targetBase?.tables;

      if (!targetBaseTables?.[item?.value as keyof typeof targetBaseTables])
        return false;

      return true;
    });
  }, [data, userCountry, country, baseId]);

  return {
    isLoading,
    isError,
    error,
    data,
    menuOptions,
  };
};

export default useBaseTablesQuery;
